import React, { Suspense, useMemo, useState } from "react";
import {
  Box,
  Alert,
  AlertIcon,
  Avatar,
  Button,
  Card,
  CardBody,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Input,
  Image,
  Select,
  SimpleGrid,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  Heading,
  useToast,
} from "@chakra-ui/react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { BadgeCheck, ClipboardCheck, FileText, LayoutDashboard, MessagesSquare, Package, PackageCheck } from "lucide-react";
import { materialMarketplaceApi as api } from "../api/materialMarketplaceApi";
import {
  MarketplacePageShell,
  MarketplaceKpiCard,
  MarketplaceSkeleton,
  MarketplaceEmptyState,
  MaterialListingCard,
  MaterialListingTable,
  MaterialSummaryCard,
  SellerSummaryCard,
  MaterialImageGallery,
  MaterialSpecificationTable,
  MaterialDocuments,
  StatusBadge,
  MarketplaceWorkflowStepper,
  Info,
} from "../components/MarketplaceCommon";
import {
  NegotiationTimeline,
  CounterOfferForm,
  ApprovalTimeline,
  GoodsReceiptForm,
} from "../components/WorkflowComponents";
import {
  backendMessage,
  money,
  quantity,
  dateTime,
} from "../utils/marketplaceFormatters";
import {
  collection,
  useMarketplaceResource,
} from "../hooks/useMarketplaceResource";

const DashboardActivityChart = React.lazy(() => import("../components/DashboardActivityChart"));

const value = (entity, ...keys) =>
  keys.reduce((current, key) => current ?? entity?.[key], undefined);
const useFilters = () => {
  const [params, setParams] = useSearchParams();
  const filters = Object.fromEntries(params.entries());
  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, item]) =>
      item ? next.set(key, item) : next.delete(key),
    );
    if (patch.q !== undefined || patch.status !== undefined)
      next.set("page", "1");
    setParams(next);
  };
  return [filters, update];
};
const ErrorState = ({ error, retry }) => (
  <MarketplaceEmptyState
    message={`Unable to load marketplace data. ${backendMessage(error, "Please try again.")}`}
    action={<Button onClick={retry}>Retry</Button>}
  />
);
const ActivityTimeline = ({ entries = [] }) => entries.length ? (
  <Stack spacing="0">{entries.map((entry, index) => <Box key={entry._id || `${entry.status}-${index}`} borderLeftWidth="2px" borderColor="teal.400" pl="4" py="3"><Text fontWeight="600" fontSize="sm">{entry.label || entry.action || String(entry.status || "Listing updated").replace(/_/g, " ")}</Text><Text fontSize="xs" color="gray.500">{dateTime(entry.createdAt || entry.timestamp || entry.updatedAt)}</Text>{entry.remarks && <Text fontSize="sm" mt="1">{entry.remarks}</Text>}</Box>)}</Stack>
) : <MarketplaceEmptyState message="No activity has been recorded yet." />;
const ListPage = ({ title, subtitle, loader, render, empty }) => {
  const [filters, update] = useFilters();
  const result = useMarketplaceResource(
    () =>
      loader({
        ...filters,
        page: filters.page || 1,
        pageSize: filters.pageSize || 20,
      }),
    [JSON.stringify(filters)],
  );
  const rows = collection(result.data);
  return (
    <MarketplacePageShell title={title} subtitle={subtitle}>
      <Stack spacing="5">
        <Card variant="outline" bg="gray.50">
          <CardBody py="3">
        <HStack wrap="wrap" spacing="3">
          <Input
            placeholder="Search"
            maxW={{ base: "100%", md: "320px" }}
            value={filters.q || ""}
            onChange={(e) => update({ q: e.target.value })}
          />
          <Select
            maxW="220px"
            placeholder="All statuses"
            value={filters.status || ""}
            onChange={(e) => update({ status: e.target.value })}
          >
            <option value="UNDER_REVIEW">Under review</option>
            <option value="NEGOTIATION_IN_PROGRESS">
              Negotiation in progress
            </option>
            <option value="PENDING_FINAL_APPROVAL">Pending approval</option>
            <option value="COMPLETED">Completed</option>
          </Select>
        </HStack>
          </CardBody>
        </Card>
        {result.loading ? (
          <MarketplaceSkeleton />
        ) : result.error ? (
          <ErrorState error={result.error} retry={result.refresh} />
        ) : rows.length ? (
          render(rows, result.data)
        ) : (
          <MarketplaceEmptyState message={empty} />
        )}
      </Stack>
    </MarketplacePageShell>
  );
};

const WorkflowSummary = ({ listing }) => {
  const snapshot = listing?.workflowSnapshot;
  if (!snapshot) return <Card><CardBody><Heading size="xs">Review workflow</Heading><Text mt="2" color="gray.500" fontSize="sm">No workflow assignment is available for this legacy listing.</Text></CardBody></Card>;
  const currentStage = snapshot.stages?.find((stage) => stage.stageNumber === listing.workflowStage) || snapshot.stages?.[0];
  return <Card><CardBody><HStack justify="space-between" align="start"><Box><Heading size="xs">Review workflow</Heading><Text fontWeight="semibold" mt="1">{snapshot.name}</Text><Text fontSize="xs" color="gray.500">Version {snapshot.version} · Stage {listing.workflowStage || currentStage?.stageNumber || 1} of {snapshot.stages?.length || 0}</Text></Box><StatusBadge status={listing.status} /></HStack><Stack mt="4" spacing="2">{(snapshot.stages || []).map((stage) => <HStack key={stage.stageNumber} p="2" borderRadius="md" bg={stage.stageNumber === listing.workflowStage ? "blue.50" : "gray.50"}><Text fontSize="sm" fontWeight="semibold">{stage.stageNumber}.</Text><Box><Text fontSize="sm">{stage.stageName}</Text><Text fontSize="xs" color="gray.500">{(stage.approverRoles || []).join(", ") || "Assigned users"}{stage.requireAllApprovers ? " · all approvers required" : ""}</Text></Box></HStack>)}</Stack><Text fontSize="xs" color="gray.500" mt="3">Snapshot captured {dateTime(snapshot.capturedAt)}</Text></CardBody></Card>;
};

export const WorkflowSettings = () => {
  const result = useMarketplaceResource(api.getWorkflows, []);
  const navigate = useNavigate();
  const toast = useToast();
  const setDefault = async (id) => { try { await api.setDefaultWorkflow(id); toast({ title: "Default workflow updated", status: "success" }); result.refresh(); } catch (error) { toast({ title: backendMessage(error), status: "error" }); } };
  const workflows = collection(result.data);
  return <MarketplacePageShell title="Review Workflows" subtitle="Configure the workflow assigned to new marketplace listings" actions={<Button colorScheme="blue" onClick={() => navigate("/novatra/marketplace/workflows/new")}>Create workflow</Button>}><Stack>{result.loading ? <MarketplaceSkeleton /> : result.error ? <ErrorState error={result.error} retry={result.refresh} /> : workflows.length ? workflows.map((workflow) => <Card key={workflow._id || workflow.id}><CardBody><HStack justify="space-between" align="start"><Box><HStack><Heading size="sm">{workflow.name}</Heading>{workflow.isDefault && <StatusBadge status="DEFAULT" />}</HStack><Text fontSize="sm" color="gray.500" mt="1">{workflow.description || "No description"}</Text><Text fontSize="xs" mt="2">Version {workflow.version || 1} · {(workflow.stages || []).length} stage(s) · {workflow.isActive === false ? "Inactive" : "Active"}</Text></Box>{workflow.isActive !== false && !workflow.isDefault && <Button size="sm" variant="outline" onClick={() => setDefault(workflow._id || workflow.id)}>Set as default</Button>}</HStack></CardBody></Card>) : <MarketplaceEmptyState message="No marketplace workflows found." />}</Stack></MarketplacePageShell>;
};

export const CreateWorkflow = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState({ name: "", description: "", isDefault: false, isActive: true, stages: [{ stageNumber: 1, stageName: "Marketplace review", approverRoles: ["SuperAdmin", "Admin"], approverUsers: [], requireAllApprovers: false }] });
  const [saving, setSaving] = useState(false);
  const updateStage = (key, value) => setForm((current) => ({ ...current, stages: [{ ...current.stages[0], [key]: value }] }));
  const save = async (event) => { event.preventDefault(); if (!form.name.trim() || !form.stages[0].stageName.trim()) return toast({ title: "Workflow name and stage name are required", status: "warning" }); setSaving(true); try { await api.createWorkflow(form); toast({ title: "Workflow created", status: "success" }); navigate("/novatra/marketplace/workflows"); } catch (error) { toast({ title: backendMessage(error), status: "error" }); } finally { setSaving(false); } };
  return <MarketplacePageShell title="Create Review Workflow" subtitle="New listings automatically use the active default workflow"><Box as="form" onSubmit={save} maxW="760px"><Stack spacing="5"><Card><CardBody><Stack spacing="4"><FormControl isRequired><FormLabel>Workflow name</FormLabel><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormControl><FormControl><FormLabel>Description</FormLabel><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormControl><FormControl><FormLabel>Stage name</FormLabel><Input value={form.stages[0].stageName} onChange={(e) => updateStage("stageName", e.target.value)} /></FormControl><FormControl><FormLabel>Approver roles</FormLabel><Select multiple value={form.stages[0].approverRoles} onChange={(e) => updateStage("approverRoles", Array.from(e.target.selectedOptions, (option) => option.value))}><option value="SuperAdmin">SuperAdmin</option><option value="Admin">Admin</option></Select></FormControl><Button type="submit" colorScheme="blue" isLoading={saving}>Create workflow</Button></Stack></CardBody></Card></Stack></Box></MarketplacePageShell>;
};

export const MarketplaceDashboard = () => {
  const dashboard = useMarketplaceResource(api.getDashboard, []);
  const listingResult = useMarketplaceResource(
    () => api.getListings({ page: 1, pageSize: 5 }),
    [],
  );
  const kpis = dashboard.data || {};
  const cards = [
    ["New Listings", kpis.newListings, false, Package],
    ["Under Review", kpis.underReview, false, ClipboardCheck],
    ["Active Negotiations", kpis.activeNegotiations, false, MessagesSquare],
    ["Pending Approvals", kpis.pendingApprovals, false, BadgeCheck],
    ["Approved Purchases", kpis.approvedPurchases, false, FileText],
    ["Pending Receipts", kpis.pendingReceipts, false, PackageCheck],
    ["Awaiting Stock Posting", kpis.awaitingStockPosting, false, Package],
    ["Total Procurement Value", kpis.totalProcurementValue, true, LayoutDashboard],
  ];
  const activity = Array.isArray(kpis.procurementActivity) ? kpis.procurementActivity : [];
  return (
    <MarketplacePageShell
      title="Marketplace Dashboard"
      subtitle="Procurement marketplace activity"
      actions={
        <>
          <Button as={Link} to="/novatra/marketplace/browse" variant="outline">
            Browse Materials
          </Button>
          <Button as={Link} to="/novatra/marketplace/listings/new" colorScheme="blue">
            Create Listing
          </Button>
        </>
      }
    >
      <Stack spacing="7">
        <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} spacing="4">
          {cards.map(([label, number, currency, icon]) => (
            <MarketplaceKpiCard
              key={label}
              label={label}
              value={number}
              currency={currency}
              icon={icon}
            />
          ))}
        </SimpleGrid>
        <Grid templateColumns={{ base: "1fr", xl: "2fr 1fr" }} gap="5">
          <Card><CardBody><Heading size="sm">Procurement Activity</Heading><Text fontSize="sm" color="gray.500" mt="1">Procurement value over time</Text><Box h="260px" mt="5">{activity.length ? <Suspense fallback={<MarketplaceSkeleton rows={3} />}><DashboardActivityChart data={activity} /></Suspense> : <MarketplaceEmptyState message="No procurement activity is available yet." />}</Box></CardBody></Card>
          <Card><CardBody><Heading size="sm">Requires Attention</Heading><Text fontSize="sm" color="gray.500" mt="1" mb="5">Items needing the next workflow action</Text><Stack spacing="3">{[["Under review", kpis.underReview, ClipboardCheck], ["Negotiations", kpis.activeNegotiations, MessagesSquare], ["Approvals", kpis.pendingApprovals, BadgeCheck]].map(([label, number, IconComponent]) => <HStack key={label} justify="space-between" p="3" bg="marketplace.muted" borderRadius="md"><HStack><Box color="teal.600"><IconComponent size={17} /></Box><Text fontSize="sm">{label}</Text></HStack><Text fontWeight="700">{number ?? 0}</Text></HStack>)}</Stack></CardBody></Card>
        </Grid>
        <Card>
          <CardBody>
          <HStack justify="space-between" mb="3">
            <Box>
              <Heading size="sm">Recent Material Listings</Heading>
              <Text fontSize="sm" color="gray.500">Latest activity across the marketplace</Text>
            </Box>
            <Button
              as={Link}
              to="/novatra/marketplace/browse"
              size="sm"
              variant="ghost"
            >
              View all
            </Button>
          </HStack>
          {listingResult.loading ? (
            <MarketplaceSkeleton rows={3} />
          ) : (
            <Stack>
              {collection(listingResult.data)
                .slice(0, 5)
                .map((item) => (
                  <MaterialListingCard
                    key={item._id || item.id}
                    listing={item}
                  />
                ))}
            </Stack>
          )}
          </CardBody>
        </Card>
      </Stack>
    </MarketplacePageShell>
  );
};
export const MarketplaceBrowse = () => (
  <ListPage
    title="Browse Materials"
    subtitle="Discover verified material availability across suppliers"
    loader={api.getListings}
    empty="No material listings found."
    render={(rows) => (
      <Stack>
        {rows.map((row) => (
          <MaterialListingCard key={row._id || row.id} listing={row} />
        ))}
      </Stack>
    )}
  />
);
export const MyMaterialListings = () => (
  <ListPage
    title="My Listings"
    subtitle="Manage submitted material listings"
    loader={(filters) => api.getListings({
      ...filters,
      // The marketplace contract scopes listings to the authenticated tenant.
      // `mine` is not a supported query parameter.
    })}
    empty="You have not created any material listings."
    render={(rows) => (
      <MaterialListingTable
        listings={rows}
        actions={(item) => (
          <HStack>
            <Button
              as={Link}
              to={`/novatra/marketplace/listings/${item._id || item.id}`}
              size="xs"
            >
              View
            </Button>
            {["DRAFT", "CLARIFICATION_REQUIRED"].includes(item.status) && (
              <Button
                as={Link}
                to={`/novatra/marketplace/listings/${item._id || item.id}/edit`}
                size="xs"
                colorScheme="blue"
              >
                Edit
              </Button>
            )}
          </HStack>
        )}
      />
    )}
  />
);

const ListingForm = ({ edit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const existing = useMarketplaceResource(
    () => (edit ? api.getListingById(id) : Promise.resolve({})),
    [edit, id],
  );
  const [form, setForm] = useState({
    materialName: "",
    category: "",
    subcategory: "",
    sellerMaterialCode: "",
    description: "",
    condition: "",
    grade: "",
    color: "",
    composition: "",
    gsm: "",
    width: "",
    length: "",
    batch: "",
    lot: "",
    availableQuantity: "",
    uom: "KG",
    minimumPurchaseQuantity: "",
    maximumPurchaseQuantity: "",
    sellerAskingRate: "",
    currency: "INR",
    priceUnit: "KG",
    negotiable: false,
    taxIncluded: false,
    location: "",
  });
  const [saving, setSaving] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imageErrors, setImageErrors] = useState([]);
  const [savedListingId, setSavedListingId] = useState(null);
  const maxImageSize = 5 * 1024 * 1024;
  const imageTypes = ["image/jpeg", "image/png", "image/webp"];
  React.useEffect(() => {
    if (edit && existing.data)
      setForm((f) => ({
        ...f,
        ...existing.data,
        location: existing.data.location?.city || existing.data.location || "",
      }));
  }, [edit, existing.data]);
  const change = (key) => (e) =>
    setForm({
      ...form,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });
  const selectImages = (event) => {
    const valid = [];
    const rejected = [];
    Array.from(event.target.files || []).forEach((file) => {
      if (!imageTypes.includes(file.type)) rejected.push(`${file.name}: unsupported image type`);
      else if (file.size > maxImageSize) rejected.push(`${file.name}: exceeds the 5 MB limit`);
      else valid.push(file);
    });
    if (valid.length > 6) {
      rejected.push(`Only 6 new images can be uploaded per save (first 6 kept)`);
      valid.splice(6);
    }
    setSelectedImages(valid);
    setImageErrors(rejected);
    event.target.value = "";
  };
  const toListingImage = (uploaded) => ({
    fileName: uploaded.filename,
    url: uploaded.publicUrl,
    mimeType: uploaded.mimeType,
    size: uploaded.size,
  });
  const submit = async (event, shouldSubmit) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const result = savedListingId
        ? await api.updateListing(savedListingId, form)
        : edit
          ? await api.updateListing(id, form)
          : await api.createListing(form);
      const listingId = result._id || result.id || savedListingId || id;
      setSavedListingId(listingId);
      if (selectedImages.length) {
        toast({ title: "Uploading images…", status: "info", duration: null, isClosable: true });
        const uploads = await Promise.all(selectedImages.map((file) => api.uploadListingImage(file)));
        const images = [...(result.images || existing.data?.images || []), ...uploads.map(toListingImage)];
        await api.updateListing(listingId, { images });
        setSelectedImages([]);
      }
      if (shouldSubmit) await api.submitListing(listingId);
      toast({
        title: shouldSubmit ? "Listing submitted" : "Listing saved",
        status: "success",
      });
      navigate("/novatra/marketplace/my-listings");
    } catch (error) {
      toast({ title: "Listing saved, but images were not fully uploaded", description: backendMessage(error), status: "error", duration: 7000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };
  if (edit && existing.loading)
    return (
      <MarketplacePageShell title="Edit Listing">
        <MarketplaceSkeleton />
      </MarketplacePageShell>
    );
  const fields = [
    ["materialName", "Material Name"],
    ["category", "Category"],
    ["subcategory", "Subcategory"],
    ["sellerMaterialCode", "Seller Material Code"],
    ["condition", "Condition"],
    ["grade", "Grade"],
    ["color", "Color"],
    ["composition", "Composition"],
    ["gsm", "GSM"],
    ["width", "Width"],
    ["length", "Length"],
    ["batch", "Batch"],
    ["lot", "Lot"],
    ["availableQuantity", "Available Quantity"],
    ["uom", "UOM"],
    ["minimumPurchaseQuantity", "Minimum Purchase Quantity"],
    ["maximumPurchaseQuantity", "Maximum Purchase Quantity"],
    ["sellerAskingRate", "Seller Asking Rate"],
    ["currency", "Currency"],
    ["priceUnit", "Price Unit"],
    ["location", "Location"],
  ];
  const fieldGroups = [
    { title: "Material identity", description: "Name and classification used to match this listing.", keys: ["materialName", "category", "subcategory", "sellerMaterialCode"] },
    { title: "Specifications", description: "Describe the material so buyers can assess fit and quality.", keys: ["condition", "grade", "color", "composition", "gsm", "width", "length", "batch", "lot"] },
    { title: "Commercial details", description: "Set quantity, pricing, and fulfilment information.", keys: ["availableQuantity", "uom", "minimumPurchaseQuantity", "maximumPurchaseQuantity", "sellerAskingRate", "currency", "priceUnit", "location"] },
  ];
  const fieldMap = Object.fromEntries(fields);

  const quickFillTestData = () => {
    setForm({
      materialName: "Polyester Fabric - Navy Blue",
      category: "Fabric",
      subcategory: "Polyester Woven Fabric",
      sellerMaterialCode: "TEST-POLY-NVY-001",
      description:
        "Dummy marketplace listing for testing the complete procurement workflow. 100% polyester woven fabric suitable for garment production.",
      condition: "New",
      grade: "A",
      color: "Navy Blue",
      composition: "100% Polyester",
      gsm: "180",
      width: "58",
      length: "1000",
      batch: "BATCH-TEST-001",
      lot: "LOT-TEST-001",
      availableQuantity: "2500",
      uom: "KG",
      minimumPurchaseQuantity: "100",
      maximumPurchaseQuantity: "2500",
      sellerAskingRate: "145",
      currency: "INR",
      priceUnit: "KG",
      negotiable: true,
      taxIncluded: false,
      location: "Bhiwandi, Maharashtra",
    });

    toast({
      title: "Test data filled",
      status: "info",
      duration: 1500,
      isClosable: true,
    });
  };

  return (
    <MarketplacePageShell
      title={edit ? "Edit Material Listing" : "Create Material Listing"}
      subtitle="Complete the material, commercial, and location details before submitting."
    >
      <Box as="form" onSubmit={(e) => submit(e, false)} maxW="1100px">
        <Stack spacing="6">
          {fieldGroups.map((group) => (
            <Card key={group.title}>
              <CardBody>
                <Heading size="sm">{group.title}</Heading>
                <Text fontSize="sm" color="gray.500" mt="1" mb="5">{group.description}</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
            {group.keys.map((key) => (
              <FormControl key={key}>
                <FormLabel>{fieldMap[key]}</FormLabel>
                <Input
                  value={form[key] ?? ""}
                  onChange={change(key)}
                  type={
                    key.includes("Quantity") ||
                    key === "sellerAskingRate" ||
                    ["gsm", "width", "length"].includes(key)
                      ? "number"
                      : "text"
                  }
                />
              </FormControl>
            ))}
                </SimpleGrid>
              </CardBody>
            </Card>
          ))}
          <Card>
            <CardBody>
          <Heading size="sm">Description</Heading>
          <Text fontSize="sm" color="gray.500" mt="1" mb="4">Add context that helps buyers evaluate the opportunity.</Text>
          <FormControl>
            <Textarea
              value={form.description || ""}
              onChange={change("description")}
            />
          </FormControl>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Heading size="sm">Material images</Heading>
              <Text fontSize="sm" color="gray.500" mt="1" mb="4">JPEG, PNG, or WEBP images up to 5 MB each. Up to 6 new images per save.</Text>
              <FormControl>
                <FormLabel>Select images</FormLabel>
                <Input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={selectImages} p="1" isDisabled={saving} />
              </FormControl>
              {selectedImages.length > 0 && <Text fontSize="sm" mt="3">{selectedImages.length} image{selectedImages.length === 1 ? "" : "s"} ready to upload.</Text>}
              {imageErrors.map((message) => <Text key={message} color="red.500" fontSize="sm" mt="2">{message}</Text>)}
              {saving && selectedImages.length > 0 && <Text color="blue.600" fontSize="sm" mt="2">Uploading images…</Text>}
            </CardBody>
          </Card>
          <HStack flexWrap="wrap">
            {!edit && process.env.NODE_ENV === "development" && (
              <Button
                type="button"
                variant="outline"
                colorScheme="purple"
                onClick={quickFillTestData}
                isDisabled={saving}
              >
                Quick Fill Test Data
              </Button>
            )}

            <Button type="submit" isLoading={saving}>
              Save draft
            </Button>

            <Button
              colorScheme="blue"
              isLoading={saving}
              onClick={(e) => submit(e, true)}
            >
              Save & submit
            </Button>
          </HStack>
        </Stack>
      </Box>
    </MarketplacePageShell>
  );
};
const IMPORT_COLUMNS = [
  "materialName", "category", "subcategory", "sellerMaterialCode", "description",
  "condition", "grade", "color", "composition", "gsm", "width", "length", "batch", "lot",
  "availableQuantity", "uom", "minimumPurchaseQuantity", "maximumPurchaseQuantity",
  "sellerAskingRate", "currency", "priceUnit", "negotiable", "taxIncluded", "location",
];

const downloadImportTemplate = () => {
  const sample = [
    "Cotton Fabric - White", "Fabric", "Cotton", "COT-WHT-001", "100% cotton woven fabric",
    "New", "A", "White", "100% Cotton", "180", "58", "1000", "BATCH-001", "LOT-001",
    "2500", "KG", "100", "2500", "145", "INR", "KG", "true", "false", "Mumbai, Maharashtra",
  ];
  const escapeCsv = (cell) => `"${String(cell).replace(/"/g, '""')}"`;
  const csv = [IMPORT_COLUMNS, sample].map((row) => row.map(escapeCsv).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "material-listings-template.csv";
  anchor.click();
  URL.revokeObjectURL(url);
};

export const ImportMaterialListings = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [file, setFile] = useState(null);
  const [submit, setSubmit] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const allowedTypes = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "text/csv",
  ];

  const selectFile = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    const extension = selected.name.split(".").pop()?.toLowerCase();
    if (!allowedTypes.includes(selected.type) && !["xlsx", "xls", "csv"].includes(extension)) {
      toast({ title: "Choose an Excel or CSV file", status: "error" });
      event.target.value = "";
      return;
    }
    setFile(selected);
    setResult(null);
  };
  const importFile = async (event) => {
    event.preventDefault();
    if (!file || importing) return;
    setImporting(true);
    try {
      const response = await api.importListings(file, { submit });
      setResult(response);
      const created = response.createdCount ?? response.created ?? response.successCount ?? 0;
      const failed = response.failedCount ?? response.failed ?? response.errorCount ?? 0;
      toast({
        title: failed ? "Import completed with errors" : "Listings imported",
        description: `${created} created${failed ? `, ${failed} failed` : ""}.`,
        status: failed ? "warning" : "success",
      });
      if (!failed && created) setFile(null);
    } catch (error) {
      toast({ title: "Could not import listings", description: backendMessage(error), status: "error", duration: 7000, isClosable: true });
    } finally {
      setImporting(false);
    }
  };
  const failures = [result?.errors, result?.failedRows, result?.failures].find(Array.isArray) || [];

  return (
    <MarketplacePageShell
      title="Import Material Listings"
      subtitle="Create multiple material listings from an Excel workbook or CSV file."
      actions={<Button variant="outline" onClick={() => navigate("/novatra/marketplace/listings/new")}>Create one listing</Button>}
    >
      <Stack spacing="5" maxW="850px">
        <Card>
          <CardBody>
            <Heading size="sm">1. Prepare your file</Heading>
            <Text mt="2" fontSize="sm" color="gray.600">Use the template headers exactly. The first worksheet is imported; one row creates one listing.</Text>
            <Button mt="4" size="sm" variant="outline" onClick={downloadImportTemplate}>Download template</Button>
            <Text mt="4" fontSize="xs" color="gray.500">Required: materialName, availableQuantity, uom, sellerAskingRate, currency, priceUnit. Boolean values accept true or false.</Text>
          </CardBody>
        </Card>
        <Card as="form" onSubmit={importFile}>
          <CardBody>
            <Heading size="sm">2. Upload and create</Heading>
            <FormControl mt="4" isRequired>
              <FormLabel>Excel or CSV file</FormLabel>
              <Input type="file" accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv" onChange={selectFile} p="1" isDisabled={importing} />
              {file && <Text mt="2" fontSize="sm">Ready to import: {file.name}</Text>}
            </FormControl>
            <FormControl display="flex" alignItems="center" mt="4">
              <Input id="submit-imported-listings" type="checkbox" w="auto" mr="2" checked={submit} onChange={(event) => setSubmit(event.target.checked)} />
              <FormLabel htmlFor="submit-imported-listings" mb="0">Submit imported listings for review</FormLabel>
            </FormControl>
            <Button mt="5" type="submit" colorScheme="blue" isLoading={importing} loadingText="Importing" isDisabled={!file}>Import listings</Button>
          </CardBody>
        </Card>
        {result && <Card>
          <CardBody>
            <Heading size="sm">Import result</Heading>
            <Text mt="2">{result.createdCount ?? result.created ?? result.successCount ?? 0} created · {result.failedCount ?? result.failed ?? result.errorCount ?? 0} failed</Text>
            {failures.length > 0 && <Stack mt="4" spacing="2">{failures.map((failure, index) => <Alert key={failure.row || failure.rowNumber || index} status="error" borderRadius="md"><AlertIcon /><Text fontSize="sm">Row {failure.row ?? failure.rowNumber ?? index + 2}: {failure.message || failure.error || "Unable to create listing"}</Text></Alert>)}</Stack>}
          </CardBody>
        </Card>}
      </Stack>
    </MarketplacePageShell>
  );
};

export const CreateMaterialListing = () => <ListingForm />;
export const EditMaterialListing = () => <ListingForm edit />;

export const MaterialListingDetails = () => {
  const { id } = useParams();
  const result = useMarketplaceResource(() => api.getListingById(id), [id]);
  const listing = result.data;
  if (result.loading)
    return (
      <MarketplacePageShell title="Material Listing">
        <MarketplaceSkeleton />
      </MarketplacePageShell>
    );
  if (result.error)
    return (
      <MarketplacePageShell title="Material Listing">
        <ErrorState error={result.error} retry={result.refresh} />
      </MarketplacePageShell>
    );
  return (
    <MarketplacePageShell
      title={listing.materialName || listing.name}
      subtitle={`Listing ID: ${listing.listingNumber || listing.listingId || id}`}
      actions={<StatusBadge status={listing.status} />}
    >
      <Stack spacing="5">
        <MarketplaceWorkflowStepper status={listing.status} />
        <WorkflowSummary listing={listing} />
        <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap="5">
          <Stack spacing="5">
            <MaterialImageGallery images={listing.images} materialName={listing.materialName || listing.name || "Material"} />
            <Tabs>
              <TabList>
                <Tab>Overview</Tab>
                <Tab>Specifications</Tab>
                <Tab>Documents</Tab>
                <Tab>Seller</Tab>
                <Tab>Activity</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
                    <Card><CardBody><Heading size="xs" mb="4">Availability</Heading><SimpleGrid columns="2" spacing="4"><Info label="Available quantity" value={quantity(value(listing, "availableQuantity", "quantity"), listing.uom)} /><Info label="Minimum purchase" value={quantity(listing.minimumPurchaseQuantity, listing.uom)} /><Info label="Maximum purchase" value={quantity(listing.maximumPurchaseQuantity, listing.uom)} /><Info label="Available from" value={dateTime(listing.availableFrom)} /></SimpleGrid></CardBody></Card>
                    <Card><CardBody><Heading size="xs" mb="4">Commercial</Heading><SimpleGrid columns="2" spacing="4"><Info label="Seller rate" value={`${money(listing.sellerAskingRate ?? listing.askingRate, listing.currency)} / ${listing.priceUnit || listing.uom || "unit"}`} /><Info label="Negotiable" value={listing.negotiable ? "Yes" : "No"} /><Info label="Tax included" value={listing.taxIncluded ? "Yes" : "No"} /><Info label="Estimated value" value={money((listing.availableQuantity ?? listing.quantity) * (listing.sellerAskingRate ?? listing.askingRate), listing.currency)} /></SimpleGrid></CardBody></Card>
                    <Card gridColumn={{ md: "span 2" }}><CardBody><Heading size="xs" mb="4">Fulfilment</Heading><SimpleGrid columns={{ base: 1, md: 3 }} spacing="4"><Info label="Location" value={listing.location?.city || listing.location} /><Info label="Batch" value={listing.batch} /><Info label="Lot" value={listing.lot} /></SimpleGrid></CardBody></Card>
                  </SimpleGrid>
                </TabPanel>
                <TabPanel>
                  <MaterialSpecificationTable
                    specifications={listing.specifications || listing}
                  />
                </TabPanel>
                <TabPanel>
                  <MaterialDocuments documents={listing.documents} />
                </TabPanel>
                <TabPanel>
                  <SellerSummaryCard seller={listing.seller} />
                </TabPanel>
                <TabPanel>
                  <ActivityTimeline entries={listing.statusHistory || listing.activity || []} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Stack>
          <Stack>
            <MaterialSummaryCard listing={listing} />
            <SellerSummaryCard seller={listing.seller} />
          </Stack>
        </Grid>
      </Stack>
    </MarketplacePageShell>
  );
};

export const MaterialReviewWorklist = () => (
  <ListPage
    title="Material Reviews"
    subtitle="Review submitted seller listings"
    loader={api.getReviewWorklist}
    empty="No material listings require review."
    render={(rows) => (
      <MaterialListingTable
        listings={rows}
        actions={(item) => (
          <Button
            as={Link}
            to={`/novatra/marketplace/reviews/${item._id || item.id || item.listingId}`}
            size="xs"
            colorScheme="blue"
          >
            Review
          </Button>
        )}
      />
    )}
  />
);
export const MaterialReviewDetails = () => {
  const { id } = useParams();
  const result = useMarketplaceResource(() => api.getReviewById(id), [id]);
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [internalMaterialId, setInternalMaterialId] = useState("");
  const [verifiedQuantity, setVerifiedQuantity] = useState("");
  const perform = async (fn, payload = {}) => {
    setBusy(true);
    try {
      await fn(id, payload);
      toast({ title: "Review updated", status: "success" });
      result.refresh();
    } catch (error) {
      toast({ title: backendMessage(error), status: "error" });
    } finally {
      setBusy(false);
    }
  };
  if (result.loading)
    return (
      <MarketplacePageShell title="Material Review">
        <MarketplaceSkeleton />
      </MarketplacePageShell>
    );
  if (result.error)
    return (
      <MarketplacePageShell title="Material Review">
        <ErrorState error={result.error} retry={result.refresh} />
      </MarketplacePageShell>
    );
  const review = result.data;
  const listing = review.listing || review;
  return (
    <MarketplacePageShell
      title="Material Review"
      subtitle={listing.listingNumber || listing.listingId}
    >
      <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap="5">
        <Stack>
          <MaterialImageGallery images={listing.images} materialName={listing.materialName || listing.name || "Material"} />
          <WorkflowSummary listing={listing} />
          <MaterialSummaryCard listing={listing} />
          <MaterialSpecificationTable
            specifications={listing.specifications || {}}
          />
          <MaterialDocuments documents={listing.documents} />
          <SellerSummaryCard seller={listing.seller} />
        </Stack>
        <Stack borderWidth="1px" borderRadius="lg" p="4">
          <Text fontWeight="semibold">Business Review Panel</Text>
          <Info
            label="Declared Quantity"
            value={quantity(
              listing.availableQuantity ?? listing.quantity,
              listing.uom,
            )}
          />
          <FormControl>
            <FormLabel>Verified Quantity</FormLabel>
            <Input
              type="number"
              value={verifiedQuantity}
              onChange={(e) => setVerifiedQuantity(e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Verification Method</FormLabel>
            <Select id="verification-method" defaultValue="Seller Confirmation">
              <option>Seller Confirmation</option>
              <option>Physical Inspection</option>
              <option>Video Verification</option>
              <option>Document Verification</option>
              <option>Existing Seller Confirmation</option>
              <option>Third Party Verification</option>
            </Select>
          </FormControl>
            <FormControl>
            <FormLabel>Internal Material ID</FormLabel>
            <Input
              placeholder="Required for approval"
              value={internalMaterialId}
              onChange={(e) => setInternalMaterialId(e.target.value)}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Reviewer Remarks</FormLabel>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </FormControl>
          <Button
            isLoading={busy}
            onClick={() => perform(api.requestClarification, { remarks })}
          >
            Request clarification
          </Button>
          <Button
            isLoading={busy}
            colorScheme="orange"
            onClick={() =>
              perform(api.verifyListing, {
                verifiedQuantity: Number(verifiedQuantity),
                verificationMethod: document.getElementById(
                  "verification-method",
                )?.value,
                remarks,
              })
            }
          >
            Verify availability
          </Button>
          <Button
            isLoading={busy}
            colorScheme="blue"
            onClick={() => internalMaterialId.trim() ? perform(api.approveForNegotiation, {
              internalMaterialId,
              internalMaterialCode: internalMaterialId,
              remarks,
            }) : toast({ title: "Internal material ID is required.", status: "warning" })}
          >
            Approve for negotiation
          </Button>
          <Button
            isLoading={busy}
            colorScheme="red"
            onClick={() => perform(api.rejectListing, { reason: remarks, remarks })}
          >
            Reject
          </Button>
        </Stack>
      </Grid>
    </MarketplacePageShell>
  );
};

const NegotiationReport = ({ rows }) => (
  <Stack spacing="3">
    {rows.map((item) => {
      const listing = item.listing || {};
      const quantityValue = item.finalQuantity ?? item.initialQuantity ?? listing.availableQuantity;
      const rate = item.finalRate ?? item.initialRate ?? listing.sellerAskingRate;
      const valueAmount = item.commercial?.finalValue ?? item.commercial?.initialValue ?? quantityValue * rate;
      return <Card key={item._id || item.id} variant="outline"><CardBody><Grid templateColumns={{ base: "1fr", md: "2fr repeat(5, 1fr) auto" }} gap="4" alignItems="center"><Box><Text fontSize="xs" color="gray.500">{listing.listingNumber || item.listingId || "—"}</Text><Text fontWeight="semibold">{listing.materialName || "Material"}</Text><Text fontSize="sm" color="gray.500">Seller: {item.seller?.name || listing.sellerName || "—"}</Text></Box><Info label="Quantity" value={quantity(quantityValue, item.uom || listing.uom)} /><Info label="Rate" value={`${money(rate, item.currency || listing.currency)} / ${item.priceUnit || listing.priceUnit || "unit"}`} /><Info label="Value" value={money(valueAmount, item.commercial?.currency || item.currency || listing.currency)} /><Info label="Offers" value={item.offerCount ?? 0} /><Info label="Started" value={dateTime(item.initiatedAt || item.createdAt)} /><Box><StatusBadge status={item.status} /><Button as={Link} to={`/novatra/marketplace/negotiations/${item._id || item.id}`} size="xs" colorScheme="blue" mt="2">View details</Button></Box></Grid></CardBody></Card>;
    })}
  </Stack>
);

export const NegotiationWorklist = () => (
  <ListPage
    title="Negotiations"
    subtitle="Current supplier and buyer negotiations"
    loader={api.getNegotiations}
    empty="No negotiations currently require action."
    render={(rows) => (
      <NegotiationReport rows={rows} />
    )}
  />
);
export const NegotiationDetails = () => {
  const { id } = useParams();
  const result = useMarketplaceResource(() => api.getNegotiation(id), [id]);
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const act = async (operation, payload = {}) => {
    setBusy(true);
    try {
      await operation(id, payload);
      toast({ title: "Negotiation updated", status: "success" });
      result.refresh();
    } catch (error) {
      toast({ title: backendMessage(error), status: "error" });
    } finally {
      setBusy(false);
    }
  };
  if (result.loading)
    return (
      <MarketplacePageShell title="Negotiation">
        <MarketplaceSkeleton />
      </MarketplacePageShell>
    );
  if (result.error)
    return (
      <MarketplacePageShell title="Negotiation">
        <ErrorState error={result.error} retry={result.refresh} />
      </MarketplacePageShell>
    );
  const negotiation = result.data;
  const listing = negotiation.listing || negotiation;
  const availableQuantity = negotiation.finalQuantity ?? negotiation.initialQuantity ?? listing.availableQuantity;
  const currentRate = negotiation.finalRate ?? negotiation.initialRate ?? listing.sellerAskingRate;
  const offers = [
    ...(negotiation.initialOffer ? [negotiation.initialOffer] : []),
    ...(negotiation.offers || negotiation.offerHistory || []).filter((offer) => offer._id !== negotiation.initialOffer?._id),
    ...(negotiation.latestOffer && negotiation.latestOffer._id !== negotiation.initialOffer?._id ? [negotiation.latestOffer] : []),
  ];
  const validOffers = offers.filter((offer) => Number(offer.quantity) > 0 && Number(offer.rate) > 0);
  const initialOffer = validOffers[0] || { quantity: negotiation.initialQuantity, rate: negotiation.initialRate };
  const latestOffer = validOffers[validOffers.length - 1] || initialOffer;
  const currentQuantity = Number(latestOffer?.quantity || availableQuantity || 0);
  const currentRateValue = Number(latestOffer?.rate || currentRate || 0);
  const currentValue = currentQuantity * currentRateValue;
  const isActionRequired = latestOffer?.offeredByType === "SELLER" && !["ACCEPTED", "REJECTED", "EXPIRED"].includes(negotiation.status);
  const displayStatus = isActionRequired ? "Awaiting Your Response" : negotiation.status === "ACCEPTED" ? "Offer Accepted" : negotiation.status === "REJECTED" ? "Offer Rejected" : "Awaiting Seller Response";
  return (
    <MarketplacePageShell
      title={listing.materialName || negotiation.materialName || "Negotiation"}
      subtitle={`Listing ${listing.listingNumber || negotiation.listingId || "—"}`}
      actions={negotiation.status && negotiation.status !== "UNKNOWN" ? <StatusBadge status={negotiation.status} /> : null}
    >
      <Stack spacing="5" maxW="1320px" mx="auto" w="100%" px={{ base: 4, md: 6 }}>
        <Text fontSize="sm" color="gray.500">Marketplace / Negotiations / {listing.listingNumber || negotiation.listingId}</Text>
        <SimpleGrid columns={{ base: 2, md: 5 }} spacing="0" borderWidth="1px" borderRadius="12px" overflow="hidden" bg="white">
          {[["Quantity", quantity(currentQuantity, negotiation.uom || listing.uom)], ["Seller ask", `${money(listing.sellerAskingRate, listing.currency)} / ${listing.priceUnit || listing.uom}`], ["Current offer", `${money(currentRateValue, negotiation.currency || listing.currency)} / ${negotiation.priceUnit || listing.priceUnit}`], ["Current value", money(currentValue, negotiation.currency || listing.currency)], ["Offers", validOffers.length]].map(([label, value]) => <Card key={label}><CardBody><Text fontSize="xs" color="gray.500" textTransform="uppercase">{label}</Text><Text fontWeight="700" mt="1">{value}</Text></CardBody></Card>)}
        </SimpleGrid>
        {isActionRequired && <Alert status="info" borderRadius="md"><AlertIcon /><Box><Text fontWeight="semibold">Action required</Text><Text fontSize="sm">The seller has proposed {money(currentRateValue, negotiation.currency || listing.currency)} / {negotiation.priceUnit || listing.priceUnit} for {quantity(currentQuantity, negotiation.uom || listing.uom)}. You can accept or submit a counter offer.</Text></Box></Alert>}
        <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap="6">
        <Stack order={{ base: 2, lg: 1 }}>
          <Card><CardBody><HStack justify="space-between"><Heading size="md">Offer History</Heading><Text fontSize="sm" color="gray.500">{validOffers.length} offer{validOffers.length === 1 ? "" : "s"}</Text></HStack><Stack mt="5"><NegotiationTimeline offers={validOffers} /></Stack></CardBody></Card>
        </Stack>
        <Stack order={{ base: 1, lg: 2 }} position={{ lg: "sticky" }} top="20" h="fit-content">
          <Card><CardBody>
          <HStack mb="4"><Avatar size="sm" name={listing.materialName} src={listing.images?.[0]?.url} /><Box><Heading size="sm">Deal Summary</Heading><Text fontSize="sm" color="gray.500">{listing.materialName}</Text></Box></HStack>
          <Stack spacing="3"><Info label="Seller" value={negotiation.seller?.name || listing.sellerName || "Not provided"} /><Info label="Available" value={quantity(listing.availableQuantity, listing.uom)} /><Info label="Current offer" value={`${money(currentRateValue, negotiation.currency || listing.currency)} / ${negotiation.priceUnit || listing.priceUnit}`} /><Info label="Current value" value={money(currentValue, negotiation.currency || listing.currency)} /><Text fontSize="sm" fontWeight="600">{displayStatus}</Text></Stack>
          </CardBody></Card>
          <Box borderWidth="1px" borderRadius="lg" p="4">
            <Text fontWeight="semibold" mb="3">
              Your response
            </Text>
            <CounterOfferForm
              verifiedQuantity={currentQuantity}
              initialQuantity={currentQuantity}
              initialRate={currentRateValue}
              loading={busy}
              onSubmit={(offer) => act(api.createOffer, offer)}
            />
            <HStack mt="3">
              <Button
                isLoading={busy}
                colorScheme="red"
                variant="outline"
                onClick={() => act(api.rejectOffer)}
              >
                Reject offer
              </Button>
              <Button
                isLoading={busy}
                colorScheme="green"
                onClick={() => act(api.acceptOffer)}
              >
                Accept deal
              </Button>
            </HStack>
          </Box>
        </Stack>
      </Grid>
      </Stack>
    </MarketplacePageShell>
  );
};

export const ApprovalWorklist = () => (
  <ListPage
    title="Purchase Approvals"
    subtitle="Procurement deals awaiting approval"
    loader={(filters) => api.getApprovals({ ...filters, status: "PENDING_APPROVAL" })}
    empty="No approvals are pending."
    render={(rows) => (
      <MaterialListingTable
        listings={rows}
        actions={(item) => (
          <Button as={Link} to={`/novatra/marketplace/approvals/${item._id || item.id || item.dealId}`} size="xs" colorScheme="blue">
            Open
          </Button>
        )}
      />
    )}
  />
);
export const ProcurementApprovalDetails = () => {
  const { id } = useParams();
  const result = useMarketplaceResource(() => api.getProcurementDeal(id), [id]);
  const toast = useToast();
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (operation, remarksRequired = false) => {
    if (remarksRequired && !remarks.trim())
      return toast({ title: "Remarks are required.", status: "warning" });
    setBusy(true);
    try {
      await operation(id, { remarks });
      toast({ title: "Approval updated", status: "success" });
      result.refresh();
    } catch (error) {
      toast({ title: backendMessage(error), status: "error" });
    } finally {
      setBusy(false);
    }
  };
  if (result.loading)
    return (
      <MarketplacePageShell title="Purchase Approval">
        <MarketplaceSkeleton />
      </MarketplacePageShell>
    );
  if (result.error)
    return (
      <MarketplacePageShell title="Purchase Approval">
        <ErrorState error={result.error} retry={result.refresh} />
      </MarketplacePageShell>
    );
  const deal = result.data;
  const qty = deal.agreedQuantity || deal.quantity;
  const rate = deal.finalRate || deal.rate;
  return (
    <MarketplacePageShell
      title="Purchase Approval"
      subtitle={`Deal ID: ${deal.dealNumber || deal.dealId || id}`}
      actions={<StatusBadge status={deal.status} />}
    >
      <Stack spacing="5">
        <SimpleGrid columns={{ base: 2, md: 5 }} spacing="3">
          {[
            ["Agreed Quantity", quantity(qty, deal.uom)],
            ["Final Rate", money(rate, deal.currency)],
            [
              "Basic Value",
              money(deal.basicValue ?? qty * rate, deal.currency),
            ],
            ["Tax", money(deal.tax, deal.currency)],
            [
              "Total Value",
              money(deal.totalValue ?? qty * rate, deal.currency),
            ],
          ].map(([label, v]) => (
            <MarketplaceKpiCard key={label} label={label} value={v} />
          ))}
        </SimpleGrid>
        <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap="5">
          <Stack>
            <MaterialSummaryCard listing={deal.listing || deal} />
            <WorkflowSummary listing={deal.listing || deal} />
            <Tabs>
              <TabList>
                <Tab>Approval Details</Tab>
                <Tab>Payment & Delivery</Tab>
                <Tab>Documents</Tab>
                <Tab>Negotiation History</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <ApprovalTimeline
                    levels={deal.approvalLevels || deal.approvals}
                  />
                </TabPanel>
                <TabPanel>
                  <Text>
                    {deal.paymentTerms ||
                      "No payment and delivery terms available."}
                  </Text>
                </TabPanel>
                <TabPanel>
                  <MaterialDocuments documents={deal.documents} />
                </TabPanel>
                <TabPanel>
                  <NegotiationTimeline
                    offers={deal.negotiation?.offers || deal.offerHistory}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Stack>
          <Stack
            borderWidth="1px"
            borderRadius="lg"
            p="4"
            h="fit-content"
            position={{ lg: "sticky" }}
            top="4"
          >
            <Text fontWeight="semibold">Approval Actions</Text>
            <Textarea
              placeholder="Remarks (required for reject or return)"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
            <Button
              isLoading={busy}
              colorScheme="green"
              onClick={() => act(api.approveDeal)}
            >
              Approve Purchase
            </Button>
            <Button
              isLoading={busy}
              colorScheme="red"
              onClick={() => act(api.rejectDeal, true)}
            >
              Reject Purchase
            </Button>
            <Button
              isLoading={busy}
              variant="outline"
              onClick={() => act(api.returnForNegotiation, true)}
            >
              Return for Negotiation
            </Button>
          </Stack>
        </Grid>
      </Stack>
    </MarketplacePageShell>
  );
};

export const PurchaseOrders = () => (
  <ListPage
    title="Purchase Orders"
    subtitle="Approved marketplace purchases"
    loader={(filters) => api.getPurchaseOrders({ ...filters, status: "CREATED" })}
    empty="No purchase orders found."
    render={(rows) => (
      <MaterialListingTable
        listings={rows}
        actions={(item) => (
          <Button
            as={Link}
            to={`/novatra/marketplace/purchase-orders/${item._id || item.id}`}
            size="xs"
            colorScheme="blue"
          >
            View PO
          </Button>
        )}
      />
    )}
  />
);
export const PurchaseOrderDetails = () => {
  const { id } = useParams();
  const result = useMarketplaceResource(() => api.getPurchaseOrder(id), [id]);
  if (result.loading)
    return (
      <MarketplacePageShell title="Purchase Order">
        <MarketplaceSkeleton />
      </MarketplacePageShell>
    );
  if (result.error)
    return (
      <MarketplacePageShell title="Purchase Order">
        <ErrorState error={result.error} retry={result.refresh} />
      </MarketplacePageShell>
    );
  const po = result.data;
  return (
    <MarketplacePageShell
      title="Purchase Order"
      subtitle={po.poNumber || po.purchaseOrderNumber || id}
      actions={<StatusBadge status={po.status} />}
    >
      <Stack spacing="5">
        <MarketplaceWorkflowStepper status={po.status} />
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing="4">
          <SellerSummaryCard seller={po.seller || po.supplier} />
          <MaterialSummaryCard listing={po.listing || po} />
          <Box borderWidth="1px" borderRadius="lg" p="4">
            <Info
              label="Purchase date"
              value={dateTime(po.purchaseDate || po.createdAt)}
            />
            <Info label="Delivery date" value={dateTime(po.deliveryDate)} />
            <Info
              label="Total value"
              value={money(po.totalValue ?? po.commercial?.totalValue, po.currency)}
            />
          </Box>
        </SimpleGrid>
        <Box borderWidth="1px" borderRadius="lg" p="4">
          <Text fontWeight="semibold" mb="3">
            Material Items
          </Text>
          <MaterialListingTable listings={po.items || [po.listing || po]} />
        </Box>
        <Box borderWidth="1px" borderRadius="lg" p="4">
          <Text fontWeight="semibold">Delivery Information</Text>
          <Text mt="2">
            {po.deliveryAddress ||
              po.deliveryTerms ||
              "No delivery information available."}
          </Text>
        </Box>
      </Stack>
    </MarketplacePageShell>
  );
};
export const GoodsReceiptDetails = () => {
  const { id } = useParams();
  const result = useMarketplaceResource(() => api.getGoodsReceipt(id), [id]);
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const submit = async (payload) => {
    setSaving(true);
    try {
      await api.createGoodsReceipt(id, payload);
      toast({ title: "Goods receipt created", status: "success" });
      result.refresh();
    } catch (error) {
      toast({ title: backendMessage(error), status: "error" });
    } finally {
      setSaving(false);
    }
  };
  const postStock = async () => {
    if (
      !window.confirm(
        "Post accepted material to stock? This will be validated by the marketplace backend.",
      )
    )
      return;
    setSaving(true);
    try {
      await api.postGoodsReceiptToStock(id);
      toast({ title: "Stock posted", status: "success" });
      result.refresh();
    } catch (error) {
      toast({ title: backendMessage(error), status: "error" });
    } finally {
      setSaving(false);
    }
  };
  if (result.loading)
    return (
      <MarketplacePageShell title="Material Receipt">
        <MarketplaceSkeleton />
      </MarketplacePageShell>
    );
  if (result.error)
    return (
      <MarketplacePageShell title="Material Receipt">
        <ErrorState error={result.error} retry={result.refresh} />
      </MarketplacePageShell>
    );
  const receipt = result.data;
  const poQty =
    receipt.purchaseOrderQuantity ??
    receipt.orderedQuantity ??
    receipt.purchaseOrder?.quantity;
  const previous =
    receipt.previouslyReceived ?? receipt.previousReceivedQuantity ?? 0;
  const pending =
    receipt.pendingQuantity ?? Number(poQty || 0) - Number(previous);
  const accepted = receipt.acceptedQuantity ?? 0;
  const currentStock = receipt.existingStock ?? 0;
  return (
    <MarketplacePageShell
      title="Material Receipt"
      subtitle={receipt.receiptNumber || id}
      actions={<StatusBadge status={receipt.status} />}
    >
      <Stack spacing="5">
        <MarketplaceWorkflowStepper status={receipt.status} />
        <SimpleGrid columns={{ base: 2, md: 3 }} spacing="3">
          {[
            ["PO Quantity", poQty],
            ["Previously Received", previous],
            ["Pending Quantity", pending],
            ["Received Quantity", receipt.receivedQuantity],
            ["Accepted Quantity", accepted],
            ["Rejected Quantity", receipt.rejectedQuantity],
          ].map(([label, v]) => (
            <MarketplaceKpiCard key={label} label={label} value={v} />
          ))}
        </SimpleGrid>
        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap="5">
          <Box borderWidth="1px" borderRadius="lg" p="4">
            <Text fontWeight="semibold" mb="3">
              Receive Material
            </Text>
            <GoodsReceiptForm
              pendingQuantity={pending}
              onSubmit={submit}
              loading={saving}
            />
          </Box>
          <Stack>
            <MaterialSummaryCard
              listing={
                receipt.listing || receipt.purchaseOrder?.listing || receipt
              }
            />
            <Box borderWidth="1px" borderRadius="lg" p="4">
              <Text fontWeight="semibold">Quality Inspection</Text>
              <Info
                label="Inspection Required"
                value={receipt.inspectionRequired ? "Yes" : "No"}
              />
              <Info
                label="Inspection Status"
                value={receipt.inspectionStatus}
              />
              <Info
                label="Inspector"
                value={receipt.inspector?.name || receipt.inspector}
              />
              <Info
                label="Quality Remarks"
                value={receipt.qualityRemarks || "—"}
              />
            </Box>
            <Box borderWidth="1px" borderRadius="lg" p="4">
              <Text fontWeight="semibold">Stock Posting Summary</Text>
              <Info label="Source" value="MARKETPLACE_PURCHASE" />
              <Info
                label="Existing Stock"
                value={quantity(currentStock, receipt.uom)}
              />
              <Info
                label="Posting"
                value={`+${quantity(accepted, receipt.uom)}`}
              />
              <Info
                label="New Stock After Posting"
                value={quantity(
                  Number(currentStock) + Number(accepted),
                  receipt.uom,
                )}
              />
              <Button
                mt="4"
                colorScheme="blue"
                isLoading={saving}
                onClick={postStock}
              >
                Post to Stock
              </Button>
            </Box>
          </Stack>
        </Grid>
      </Stack>
    </MarketplacePageShell>
  );
};
export const GoodsReceipts = () => (
  <ListPage title="Material Receipts" subtitle="Goods receipts created for marketplace purchase orders" loader={api.getGoodsReceipts} empty="No goods receipts found." render={(rows) => <MaterialListingTable listings={rows} actions={(item) => <Button as={Link} to={`/novatra/marketplace/receipts/${item._id || item.id || item.goodsReceiptId}`} size="xs" colorScheme="blue">View receipt</Button>} />} />
);
export const StockPostingWorklist = () => (
  <ListPage
    title="Stock Posting"
    subtitle="Accepted marketplace material waiting for inventory posting"
    loader={(filters) => api.getStockWorklist({ ...filters, status: "READY_FOR_STOCK" })}
    empty="No receipts are waiting for stock posting."
    render={(rows) => (
      <MaterialListingTable
        listings={rows}
        actions={(item) => (
          <Button
            as={Link}
            to={`/novatra/marketplace/receipts/${item.goodsReceiptId || item._id || item.id}`}
            size="xs"
            colorScheme="blue"
          >
            Open receipt
          </Button>
        )}
      />
    )}
  />
);
export const CompletedProcurements = () => (
  <ListPage
    title="Completed Procurements"
    subtitle="Completed marketplace procurements and stock traceability"
    loader={(filters) => api.getGoodsReceipts({ ...filters, status: "POSTED" })}
    empty="No completed procurements found."
    render={(rows) => (
      <MaterialListingTable
        listings={rows}
        actions={(item) => (
          <Button as={Link} to={`/novatra/marketplace/receipts/${item._id || item.id}`} size="xs" colorScheme="blue">
            View receipt
          </Button>
        )}
      />
    )}
  />
);
